import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Home, MapPin, Star, Loader2, MessageCircle, BedDouble, ArrowRight,
  Building2, Instagram, Globe, Quote, LayoutGrid,
} from "lucide-react";
import apiClient from "@/lib/api";
import { setMiniSiteContext } from "@/lib/minisiteContext";
import SanquaBadge from "@/components/property/SanquaBadge";
import MiniSitePropertyCard from "@/components/minisite/MiniSitePropertyCard";
import ShareButtons from "@/components/minisite/ShareButtons";

const DEFAULT_HERO =
  "https://images.unsplash.com/photo-1750420556288-d0e32a6f517b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxjb3p5JTIwbW9kZXJuJTIwYmVkcm9vbSUyMGludGVyaW9yJTIwdHJvcGljYWx8ZW58MHx8fHwxNzg3NTU5MDQ0fDA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.5, ease: "easeOut" } }),
};

function waHref(phone) {
  const digits = String(phone || "").replace(/[^0-9]/g, "").replace(/^0/, "62");
  return `https://wa.me/${digits}`;
}

function setMeta(name, content, isProperty = false) {
  if (!content) return;
  const attr = isProperty ? "property" : "name";
  let tag = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

export default function MiniSitePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    document.title = "Web-Site — AVASA Coliving";
    setMiniSiteContext({ slug });
    apiClient
      .get(`/public/minisite/${slug}`)
      .then((r) => {
        setData(r.data);
        setStatus("ok");
        setMiniSiteContext({ slug, owner_name: r.data.owner_name, logo_url: r.data.logo_url || null });
        const title = `${r.data.headline || r.data.owner_name} — AVASA Coliving`;
        document.title = title;
        setMeta("description", r.data.tagline || `Temukan hunian ${r.data.owner_name} di AVASA Coliving.`);
        setMeta("og:title", title, true);
        setMeta("og:description", r.data.tagline || "Hunian nyaman di AVASA Coliving", true);
        const cover = r.data.cover_url || (r.data.featured || []).find((p) => p.cover_url)?.cover_url;
        if (cover) setMeta("og:image", cover, true);
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  const featured = data?.featured || [];
  const stats = data?.stats || {};
  const reviews = data?.reviews || [];
  const heroImg = useMemo(
    () => data?.cover_url || featured.find((p) => p.cover_url)?.cover_url || DEFAULT_HERO,
    [data, featured]
  );
  const primaryWa = (data?.whatsapp_contacts || [])[0];
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/properti/${slug}` : "";

  const accent = data?.theme?.accent_color || null;
  const accentBg = accent ? { backgroundColor: accent } : undefined;
  const accentText = accent ? { color: accent } : undefined;
  const heroCta = data?.hero?.cta_text || "Lihat Semua Hunian";
  const overlay = data?.hero?.overlay ?? 0.55;
  const orderedSections = (data?.sections || []).filter((s) => s.enabled);
  const cfgOf = (key) => (data?.sections || []).find((s) => s.key === key) || {};

  if (status === "loading") {
    return (
      <div className="min-h-screen grid place-items-center bg-background" data-testid="minisite-loading">
        <Loader2 className="animate-spin text-primary" size={30} />
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6 text-center" data-testid="minisite-notfound">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-2">Web-site tidak ditemukan</h1>
          <p className="text-muted-foreground mb-6">Tautan mungkin salah atau owner belum mengaktifkan web-site.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">
            <Home size={16} /> Ke Beranda AVASA
          </Link>
        </div>
      </div>
    );
  }

  if (data?.locked) {
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6 text-center" data-testid="minisite-locked">
        <div className="max-w-md">
          <div className="mx-auto grid place-items-center w-16 h-16 rounded-2xl bg-secondary text-muted-foreground mb-4"><Home size={26} /></div>
          <h1 className="font-heading text-2xl font-bold mb-2">Web-site ini sedang tidak aktif</h1>
          <p className="text-muted-foreground mb-6">Halaman {data.owner_name || "pemilik"} untuk sementara tidak tersedia. Silakan kembali lagi nanti.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">
            <Home size={16} /> Ke Beranda AVASA
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="minisite-page">
      {/* HERO — digital storefront */}
      <section className="relative overflow-hidden min-h-[70vh] flex flex-col">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Hunian" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, rgba(0,0,0,${Math.min(overlay + 0.15, 0.95)}), rgba(0,0,0,${Math.max(overlay - 0.2, 0)}), rgba(0,0,0,${overlay}))` }} />
        </div>

        <motion.div
          className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-8 flex-1 flex flex-col justify-between text-center"
          initial="hidden" animate="show" variants={fadeUp}
        >
          <Link to="/" data-testid="minisite-avasa-link"
            className="mx-auto inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-md text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-white/25 transition-colors">
            <Building2 size={14} /> AVASA Coliving
          </Link>

          <div className="mt-8">
            {data.logo_url && (
              <img src={data.logo_url} alt={data.owner_name}
                data-testid="minisite-logo"
                className="mx-auto w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-4 border-white/80 shadow-xl mb-5 bg-white" />
            )}
            <div className="flex flex-wrap items-center justify-center gap-3">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05] drop-shadow-[0_2px_16px_rgba(0,0,0,0.5)]" data-testid="minisite-owner-name">
                {data.headline || data.owner_name}
              </h1>
              {data.any_sanqua_partner && <SanquaBadge testid="minisite-sanqua-badge" />}
            </div>
            {data.service_area && (
              <p className="mt-4 inline-flex items-center gap-1.5 text-sm text-white/90 bg-white/10 backdrop-blur-md rounded-full px-4 py-1.5" data-testid="minisite-service-area">
                <MapPin size={14} /> {data.service_area}
              </p>
            )}
            {data.tagline && (
              <p className="mt-5 text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl mx-auto drop-shadow-[0_1px_10px_rgba(0,0,0,0.5)]" data-testid="minisite-tagline">
                {data.tagline}
              </p>
            )}
          </div>

          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to={`/properti/${slug}/hunian`} data-testid="minisite-hero-all" style={accentBg}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-105 active:scale-95 transition-transform">
              {heroCta} <ArrowRight size={18} />
            </Link>
            {primaryWa && (
              <a href={waHref(primaryWa.phone)} target="_blank" rel="noreferrer" data-testid="minisite-wa-hero"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-colors">
                <MessageCircle size={18} /> Hubungi {primaryWa.label}
              </a>
            )}
          </div>
        </motion.div>
      </section>

      {/* CONFIGURABLE SECTIONS — order & visibility from CMS */}
      {orderedSections.map((sec) => {
        if (sec.key === "stats") {
          return (
            <section key="stats" className="border-b border-border bg-card">
              <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="minisite-stats">
                <Stat label="Properti" value={stats.property_count || 0} icon={Building2} />
                <Stat label="Kamar Tersedia" value={stats.available_rooms || 0} icon={BedDouble} />
                <Stat label="Area" value={(stats.cities || []).length || 0} icon={MapPin} />
                <Stat label="Rating" value={stats.rating ? `${stats.rating}` : "—"} sub={stats.review_count ? `${stats.review_count} ulasan` : ""} icon={Star} />
              </div>
            </section>
          );
        }
        if (sec.key === "about") {
          if (!data.about) return null;
          return (
            <section key="about" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center" data-testid="minisite-about">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary" style={accentText}>{sec.title || "Tentang Kami"}</span>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed whitespace-pre-line">{data.about}</p>
            </section>
          );
        }
        if (sec.key === "featured") {
          return (
            <section key="featured" className="bg-secondary/40 border-y border-border">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
                <div className="flex flex-wrap items-end justify-between gap-4">
                  <div className="max-w-xl">
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary" style={accentText}>{sec.title || "Hunian Pilihan"}</span>
                    <h2 className="mt-3 font-heading text-3xl lg:text-4xl font-semibold tracking-tight">{sec.subtitle || "Beberapa hunian unggulan kami"}</h2>
                  </div>
                  <Link to={`/properti/${slug}/hunian`} data-testid="minisite-see-all"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary/40 text-primary font-semibold hover:bg-primary/5 transition-colors" style={accentText}>
                    <LayoutGrid size={16} /> Lihat Semua Hunian
                  </Link>
                </div>
                {featured.length === 0 ? (
                  <p className="mt-12 text-muted-foreground" data-testid="minisite-empty">Belum ada properti yang dipublikasikan.</p>
                ) : (
                  <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6" data-testid="minisite-property-grid">
                    {featured.map((p, i) => (
                      <motion.div key={p.id} custom={i % 3} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} variants={fadeUp}>
                        <MiniSitePropertyCard card={p} index={i} />
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          );
        }
        if (sec.key === "reviews") {
          if (reviews.length === 0) return null;
          return (
            <section key="reviews" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="minisite-reviews">
              <div className="max-w-xl">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary" style={accentText}>{sec.title || "Kata Penghuni"}</span>
                <h2 className="mt-3 font-heading text-3xl lg:text-4xl font-semibold tracking-tight">{sec.subtitle || "Ulasan asli dari penghuni"}</h2>
              </div>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {reviews.map((rv, i) => (
                  <motion.div key={rv.id} custom={i % 3} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} variants={fadeUp}
                    className="bg-card border border-border rounded-2xl p-6" data-testid={`minisite-review-${i}`}>
                    <Quote size={22} className="text-primary/30 mb-3" />
                    <div className="flex items-center gap-1 mb-2">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} size={14} className={n <= Math.round(rv.overall_rating) ? "text-accent fill-accent" : "text-muted-foreground/30"} />
                      ))}
                    </div>
                    {rv.comment && <p className="text-sm text-muted-foreground leading-relaxed line-clamp-5">"{rv.comment}"</p>}
                    <p className="mt-4 text-sm font-semibold">{rv.customer_name || "Penghuni"}</p>
                    {rv.property_name && <p className="text-xs text-muted-foreground">{rv.property_name}</p>}
                  </motion.div>
                ))}
              </div>
            </section>
          );
        }
        if (sec.key === "cta") {
          return (
            <section key="cta" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
              <div className="rounded-[2rem] bg-primary text-primary-foreground p-8 sm:p-12 lg:p-16 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, white 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
                <div className="relative z-10 max-w-2xl">
                  <h2 className="font-heading text-3xl lg:text-4xl font-semibold tracking-tight">{sec.title || "Tertarik dengan hunian kami?"}</h2>
                  <p className="mt-4 text-primary-foreground/80 leading-relaxed">{sec.subtitle || "Hubungi langsung atau pilih kamar dan mulai booking — tanpa perlu membuat akun terlebih dahulu."}</p>
                  <div className="mt-8 flex flex-wrap gap-3" data-testid="minisite-wa-list">
                    <Link to={`/properti/${slug}/hunian`} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-primary font-semibold hover:scale-105 active:scale-95 transition-transform">
                      <LayoutGrid size={18} /> Lihat Semua Hunian
                    </Link>
                    {(data.whatsapp_contacts || []).map((c, i) => (
                      <a key={i} href={waHref(c.phone)} target="_blank" rel="noreferrer" data-testid={`minisite-wa-${i}`}
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-colors">
                        <MessageCircle size={18} /> {c.label}
                      </a>
                    ))}
                  </div>
                  <div className="mt-8 pt-6 border-t border-primary-foreground/20">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground/70 mb-3">Bagikan web-site ini</p>
                    <ShareButtons url={shareUrl} title={`${data.owner_name} — AVASA Coliving`} />
                  </div>
                </div>
              </div>
            </section>
          );
        }
        // custom text section
        if (!sec.content && !sec.title) return null;
        return (
          <section key={sec.key} className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid={`minisite-custom-${sec.key}`}>
            {sec.title && <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-center">{sec.title}</h2>}
            {sec.content && <p className="mt-5 text-base text-muted-foreground leading-relaxed whitespace-pre-line text-center">{sec.content}</p>}
          </section>
        );
      })}

      {/* FOOTER */}
      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <p className="font-heading font-semibold">{data.owner_name}</p>
            <p className="text-sm text-muted-foreground">Didukung oleh AVASA Coliving</p>
          </div>
          <div className="flex items-center gap-3">
            {data.instagram && (
              <a href={data.instagram.startsWith("http") ? data.instagram : `https://instagram.com/${data.instagram.replace("@", "")}`}
                target="_blank" rel="noreferrer" data-testid="minisite-instagram"
                className="grid place-items-center w-10 h-10 rounded-full border border-border hover:bg-secondary/60 transition-colors">
                <Instagram size={17} />
              </a>
            )}
            {data.website && (
              <a href={data.website.startsWith("http") ? data.website : `https://${data.website}`}
                target="_blank" rel="noreferrer" data-testid="minisite-website"
                className="grid place-items-center w-10 h-10 rounded-full border border-border hover:bg-secondary/60 transition-colors">
                <Globe size={17} />
              </a>
            )}
            <Link to="/" className="text-sm font-semibold text-primary inline-flex items-center gap-1.5">
              <Home size={15} /> avasa.id
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value, sub, icon: Icon }) {
  return (
    <div className="text-center">
      <span className="grid place-items-center w-9 h-9 rounded-lg bg-secondary text-primary mx-auto mb-2"><Icon size={17} /></span>
      <p className="font-heading text-2xl font-bold leading-none">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}{sub ? ` · ${sub}` : ""}</p>
    </div>
  );
}
